import { useQuery } from '@tanstack/react-query'

import {
  businessDayRegisterQueryOptions,
  chartOfAccountsQueryOptions,
  classificationSchemesQueryOptions,
  dailyBalancesQueryOptions,
  driftAlertsQueryOptions,
  glAccountQueryOptions,
  journalListQueryOptions,
  journalQueryOptions,
  ledgerAuditTrailQueryOptions,
  postingPeriodsQueryOptions,
  trialBalanceQueryOptions,
  type LedgerAuditQuery,
} from '@/api/ledger-api'
import type {
  LedgerBusinessDayQuery,
  LedgerDailyBalanceQuery,
  LedgerDriftAlertQuery,
  LedgerGlAccountQuery,
  LedgerJournalQuery,
  LedgerPostingPeriodQuery,
  LedgerTrialBalanceQuery,
} from '@/api/ledger-types'
import { businessDayQueryOptions, myBranchesQueryOptions } from '@/api/reference-api'
import type { Branch } from '@/api/types'

/**
 * Reads behind the ledger screens.
 *
 * Thin by design: each hook is a `useQuery` over the options declared in `ledger-api.ts`, so the
 * key, the normalisation and the staleness live next to the request rather than being decided
 * screen by screen. The one thing that is decided here is `enabled`, and it is always driven by a
 * permission or by a submitted intent — never by "the screen is open". A read fired without the
 * permission behind it puts a 403 in the console and, worse, tempts a screen into reading "nothing
 * here" out of a refusal.
 *
 * The business day is read through `reference-api`'s query rather than one of this module's own. It
 * is the same endpoint and the same cache entry the application header uses, so opening or closing
 * a day on a ledger screen corrects the header in the same stroke — and the two can never sit side
 * by side showing different dates for one branch.
 */

const NO_BRANCHES: Branch[] = []

export interface LedgerBranchReach {
  branches: Branch[]
  homeBranchId: string | null
  /**
   * True where the user may operate outside the branches listed. It does **not** mean the list is
   * complete: the endpoint builds `items` from the home branch plus explicit grants, and there is
   * no branch registry to enumerate the rest from. For a head-office holder the list is a floor,
   * and every screen that iterates it says so.
   */
  canAccessAllBranches: boolean
  /** False while no module owns branches as records, which is what makes every code and name null. */
  directoryAvailable: boolean
  isPending: boolean
  isError: boolean
}

/** The branches this user can act in, with the two facts that qualify the list. */
export function useLedgerBranchReach(): LedgerBranchReach {
  const query = useQuery(myBranchesQueryOptions())

  return {
    branches: query.data?.items ?? NO_BRANCHES,
    homeBranchId: query.data?.homeBranchId ?? null,
    canAccessAllBranches: query.data?.canAccessAllBranches ?? false,
    directoryAvailable: query.data?.directoryAvailable ?? false,
    isPending: query.isPending,
    isError: query.isError,
  }
}

/**
 * The current business day for one branch.
 *
 * One request per branch, because that is what the endpoint offers — it answers for a single branch
 * and there is no collection form of it. A head-office reader with four branch cards makes four
 * requests, which is the honest cost of the contract as published, and they share the cache with
 * the header's own read of whichever branch is selected.
 */
export function useBranchBusinessDay(branchId: string) {
  return useQuery(businessDayQueryOptions(branchId))
}

/** The register of trading days — the history a current-day card cannot show. */
export function useBusinessDayRegister(query: LedgerBusinessDayQuery, enabled = true) {
  return useQuery(businessDayRegisterQueryOptions(query, enabled))
}

/** Journal entries. Gated on `ledger.journal.view`, which the caller passes through `enabled`. */
export function useJournalList(query: LedgerJournalQuery, enabled = true) {
  return useQuery(journalListQueryOptions(query, enabled))
}

/** One entry with its lines. Fires only once a row has actually been chosen. */
export function useJournal(journalPublicId: string | null, enabled = true) {
  return useQuery(journalQueryOptions(journalPublicId ?? '', enabled && journalPublicId !== null))
}

/** The chart of accounts as a tree. */
export function useChartOfAccounts(query: LedgerGlAccountQuery, enabled = true) {
  return useQuery(chartOfAccountsQueryOptions(query, enabled))
}

/** One account, its subtree, and its balance as at a date. */
export function useGlAccount(
  code: string | null,
  options: { branchId?: string | null; asOf?: string | null } = {},
  enabled = true,
) {
  return useQuery(glAccountQueryOptions(code ?? '', options, enabled && code !== null))
}

/**
 * The trial balance.
 *
 * `enabled` carries the permission *and* the operator's intent: the report is produced for a stated
 * range as at a stated moment, so it is run when asked for rather than on arrival at the screen.
 */
export function useTrialBalance(query: LedgerTrialBalanceQuery, enabled = true) {
  return useQuery(trialBalanceQueryOptions(query, enabled))
}

/** The stored daily rollups a trial balance is built from. */
export function useDailyBalances(query: LedgerDailyBalanceQuery, enabled = true) {
  return useQuery(dailyBalancesQueryOptions(query, enabled))
}

/** The accounting periods and their close state. */
export function usePostingPeriods(query: LedgerPostingPeriodQuery, enabled = true) {
  return useQuery(postingPeriodsQueryOptions(query, enabled))
}

/** The balance drift register, unresolved first. */
export function useDriftAlerts(query: LedgerDriftAlertQuery, enabled = true) {
  return useQuery(driftAlertsQueryOptions(query, enabled))
}

/**
 * The unresolved alarms standing against one branch and date — the ones that block its day close.
 *
 * A read of the same register with the filter pinned, kept as its own hook because the close screen
 * asks a narrower question than the register screen does and both should not have to agree on how
 * to phrase it.
 */
export function useBlockingDriftAlerts(branchId: string | null, businessDate: string | null, enabled: boolean) {
  return useQuery(
    driftAlertsQueryOptions(
      {
        branchId,
        fromDate: businessDate,
        toDate: businessDate,
        resolution: 'Unresolved',
        page: 1,
        pageSize: 50,
      },
      enabled && branchId !== null && businessDate !== null,
    ),
  )
}

/**
 * The classification schemes, when the user may read them.
 *
 * `enabled` is driven by the permission rather than by the screen being open: the list belongs to
 * the loans module and is gated on `loans.view`, which a ledger role need not hold.
 */
export function useClassificationSchemes(enabled: boolean) {
  return useQuery(classificationSchemesQueryOptions(enabled))
}

/** The audit trail for one entity. Fires only once a lookup has actually been submitted. */
export function useLedgerAuditTrail(query: LedgerAuditQuery | null, enabled: boolean) {
  return useQuery(
    ledgerAuditTrailQueryOptions(
      query ?? { entityType: 'none', entityId: 'none' },
      enabled && query !== null,
    ),
  )
}
